import { AgentPresenceMetricAgent } from 'lib-dashboard-types';
import { Media } from 'lib-shared-types-channels';
import { useMemo } from 'react';

const MEDIA_TYPES: readonly Media[] = [
  'email',
  'interactivechat',
  'messaging',
  'speak',
];

type AgentPresenceMetricData = {
  total: number;
  agents: string[];
  media_type: Record<Media, { total: number; agents: string[] }>;
};

type UseDashboardAgentPresenceMetricResult = {
  online: AgentPresenceMetricData;
  available: AgentPresenceMetricData;
};

const createAgentPresenceMetricData = (): AgentPresenceMetricData => ({
  total: 0,
  agents: [],
  media_type: {
    email: { total: 0, agents: [] },
    interactivechat: { total: 0, agents: [] },
    messaging: { total: 0, agents: [] },
    speak: { total: 0, agents: [] },
  },
});

/**
 * A hook that derives aggregate online/available counts, per-media-type
 * breakdowns and lists of available and online agent IDs from a list of agents
 * with dashboard presence information.
 */
export function useDashboardAgentPresenceMetric(
  agents?: readonly AgentPresenceMetricAgent[],
): UseDashboardAgentPresenceMetricResult {
  return useMemo(() => {
    const result: UseDashboardAgentPresenceMetricResult = {
      online: createAgentPresenceMetricData(),
      available: createAgentPresenceMetricData(),
    };

    if (!agents?.length) {
      return result;
    }

    for (const agent of agents) {
      if (agent.online) {
        result.online.total++;
        result.online.agents.push(agent.agent_id);
      }

      if (agent.available) {
        result.available.total++;
        result.available.agents.push(agent.agent_id);
      }

      for (const mediaType of MEDIA_TYPES) {
        const media = agent.media[mediaType];

        if (media?.online) {
          result.online.media_type[mediaType].total++;
          result.online.media_type[mediaType].agents.push(agent.agent_id);
        }
        if (media?.available) {
          result.available.media_type[mediaType].total++;
          result.available.media_type[mediaType].agents.push(agent.agent_id);
        }
      }
    }

    return result;
  }, [agents]);
}
